import { OptionItemSelector } from '@/src/app/main/(tabs)/card/card-management/payment/optionItemSelector'
import { PaymentCardInformation } from '@/src/app/main/(tabs)/card/card-management/_common/paymentCardInformation'
import { Button } from '@/src/common/components/Button'
import { ScrollView, StyleSheet } from 'react-native'
import { usePaymentScreen } from '@/src/experiences/card-payments/hooks/usePaymentScreen'
import { CardPaymentsE2E } from '@/src/experiences/card-payments/e2e'
import { CardPaymentsComponents as Components } from '@/src/experiences/card-payments/components'
import { provideCardPlatform } from '@/src/experiences/card-platform/provideCardPlatform'
import type { BasePaymentMethod } from '@/src/experiences/card-payments/providePaymentMethods'
import type { ComponentProps, FunctionComponent } from 'react'
import { LegacyAutoPayReview } from '@/src/experiences/card-payments/screens/AutoPayReview/LegacyAutoPayReview'
import { PlatformAutoPayReview } from '@/src/experiences/card-payments/screens/AutoPayReview/PlatformAutoPayReview'

export function useAutoPayReviewScreenProps() {
  return usePaymentScreen(
    'autoPay/confirmation',
    'Review Auto-pay',
    '/main/card/card-management/payment/autoPay/fundingSource',
  )
}

export type AutoPayViewScreenProps = ReturnType<typeof useAutoPayReviewScreenProps>

export type AutoPayReviewComponentType = FunctionComponent<{
  account?: BasePaymentMethod
  day?: ComponentProps<typeof OptionItemSelector>['title']
  paymentStrategy?: ComponentProps<typeof OptionItemSelector>['title']
  loading?: boolean
  autoPayViewScreenProps: AutoPayViewScreenProps
  createRecurringPayment?: () => void
}>

const AutoPayReview = provideCardPlatform<AutoPayReviewComponentType>(({ ToWrap, platform }) => {
  const autoPayViewScreenProps = useAutoPayReviewScreenProps()

  switch (platform) {
    case 'HIGHNOTE':
      return (
        <LegacyAutoPayReview
          AutoPayReviewComponent={ToWrap}
          autoPayViewScreenProps={autoPayViewScreenProps}
        />
      )
    case 'LOANPRO':
      return (
        <PlatformAutoPayReview
          AutoPayReviewComponent={ToWrap}
          autoPayViewScreenProps={autoPayViewScreenProps}
        />
      )
    default:
      platform satisfies undefined
      return <ToWrap loading autoPayViewScreenProps={autoPayViewScreenProps} />
  }
})(
  ({
    account,
    day,
    paymentStrategy,
    autoPayViewScreenProps: { Modal },
    createRecurringPayment,
    loading,
  }) =>
    Modal({
      children: (
        <ScrollView>
          <PaymentCardInformation title="Review Auto-pay" containerStyle={styles.container}>
            <OptionItemSelector hideIcon label="Amount" title={paymentStrategy} />
            <OptionItemSelector hideIcon label="Date" title={day} />
            <OptionItemSelector
              hideIcon
              label="Bank Account"
              title={account?.last4}
              withBorder={false}
            />
          </PaymentCardInformation>
          <Button
            style={styles.ctaButton}
            onPress={createRecurringPayment}
            title="Set up Auto-pay"
            testID={CardPaymentsE2E.Ids.Press.SetAutoPay}
            disabled={loading}
            loading={loading}
          />
          <Components.Agreement />
        </ScrollView>
      ),
    }),
)

const styles = StyleSheet.create({
  container: { marginHorizontal: 16, position: 'relative' },
  ctaButton: { marginVertical: 16 },
})

export const AutoPayReviewScreen = {
  name: 'auto-pay-schedule',
  Screen: AutoPayReview,
} as const
